import { zOrderSchemaDom, zCreateOrderSchemaDom, } from "@/types/order/order.domain";
import { zOrderSchemaDto, zCreateOrderSchemaDto, } from "@/types/order/order.dto";
import { AddressInfo } from "@/types/zAddressInfo";
import { zUpdateOrderSchemaDto, } from "@/types/order/order.dto";
import { OrderStatus } from "@/types/order/order.common";
import dayjs from "dayjs";
/**
 * Маппер для Order: DTO <-> Domain
 * Преобразует плоские адресные поля в инкапсулированный класс AddressInfo и обратно
 * Поддерживает как старые представления (vehicleModelName/vehicleManufacturerName),
 * так и новое v_orders_list (vehicleModel/vehicleBrand/vehicleStateNumber и т.д.)
 */
export const OrderMapper = {
    /**
     * Преобразование из DTO (плоская структура) в Domain (с инкапсулированным addressInfo)
     */
    toDomain: (dto) => {
        try {
            const rawDto = { ...dto };
            // Robust Status Validation
            const isValidStatus = Object.values(OrderStatus).includes(rawDto.status);
            if (!isValidStatus) {
                console.warn("OrderMapper: Invalid/Missing status:", rawDto.status, "ID:", rawDto.id, "- Defaulting to NEW");
                rawDto.status = OrderStatus.NEW;
            }
            const safeDto = zOrderSchemaDto.parse(rawDto);
            const addressInfo = AddressInfo.fromPlainObject(safeDto);
            // Извлекаем vehicleInfo: поддерживаем как старые поля, так и новые из v_orders_list
            const hasVehicleData = safeDto.vehicleModel || safeDto.vehicleBrand ||
                safeDto.vehicleModelName || safeDto.vehicleManufacturerName ||
                safeDto.idVehicle;
            const vehicleInfo = hasVehicleData
                ? {
                    // Новые поля из v_orders_list (приоритет)
                    idVehicle: safeDto.idVehicle ?? undefined,
                    brand: safeDto.vehicleBrand ?? undefined,
                    model: safeDto.vehicleModel ?? undefined,
                    stateNumber: safeDto.vehicleStateNumber ?? undefined,
                    dcPort: safeDto.vehicleDcPort ?? undefined,
                    batteryCapacity: safeDto.vehicleBatteryCapacity ?? undefined,
                    // Старые поля (обратная совместимость со старыми представлениями)
                    manufacturer: safeDto.vehicleManufacturerName ?? undefined,
                    connectorType: safeDto.vehicleConnectorType ?? undefined,
                }
                : undefined;
            // Извлекаем clientInfo из v_orders_list
            const hasClientData = safeDto.clientFirstName || safeDto.clientLastName || safeDto.clientPhone;
            const clientInfo = hasClientData
                ? {
                    firstName: safeDto.clientFirstName ?? undefined,
                    lastName: safeDto.clientLastName ?? undefined,
                    phone: safeDto.clientPhone ?? undefined,
                    completedOrdersCount: safeDto.clientCompletedOrdersCount ?? undefined,
                }
                : undefined;
            // Извлекаем chargingStats из v_orders_list
            const hasChargingStats = safeDto.historicalChargingTimeSeconds != null ||
                safeDto.completedChargesCount != null ||
                safeDto.planned_charging_time_seconds != null ||
                safeDto.estimated_charging_time_seconds != null ||
                safeDto.chargingStartAt != null;
            const chargingStats = hasChargingStats
                ? {
                    historicalChargingTimeSeconds: safeDto.historicalChargingTimeSeconds ?? undefined,
                    completedChargesCount: safeDto.completedChargesCount ?? undefined,
                    plannedChargingTimeSeconds: safeDto.planned_charging_time_seconds ?? undefined,
                    estimatedChargingTimeSeconds: safeDto.estimated_charging_time_seconds ?? undefined,
                    chargingStartAt: safeDto.chargingStartAt ?? undefined,
                }
                : undefined;
            // Извлекаем routeInfo из v_orders_list (если поля не были установлены явно)
            const routeInfo = safeDto.routeId
                ? {
                    routeId: safeDto.routeId,
                    plannedStartTime: safeDto.routePlannedStartTime ?? null,
                    status: safeDto.routeStatus,
                }
                : undefined;
            // FR-007: строка view несёт payerType, строка таблицы orders (getOrder) — только corp_company_id.
            const corpCompanyId = safeDto.corpCompanyId ?? safeDto.corp_company_id ?? null;
            const payerType = safeDto.payerType ?? (corpCompanyId ? "COMPANY" : "PRIVATE");
            const orderFields = zOrderSchemaDom.parse({
                ...safeDto,
                corpCompanyId,
                payerType,
                addressInfo: addressInfo.toPlainObject(),
                scheduledDate: dayjs(safeDto.scheduledDate),
                vehicleInfo,
            });
            return {
                ...orderFields,
                addressInfo,
                vehicleInfo,
                clientInfo,
                chargingStats,
                routeInfo,
                // FR-007: строка без колонок плательщика (создана до выката корп-схемы)
                // считается частной — отдельного «неизвестного» состояния нет (UC-101 BR-9).
                isCorporate: payerType === "COMPANY",
            };
        }
        catch (e) {
            console.error("OrderMapper validation error", e);
            throw e;
        }
    },
    /**
     * Преобразование из Domain (с AddressInfo) в DTO (плоская структура)
     */
    toDto: (domain) => {
        const safeDomain = zOrderSchemaDom.parse(domain);
        return zOrderSchemaDto.parse({
            ...safeDomain,
            ...domain.addressInfo.toPlainObject(),
            scheduledDate: domain.scheduledDate?.toISOString(),
        });
    },
    /**
     * Специализированный метод для CreateOrderData
     */
    toCreateOrderDto: (domain) => {
        const safeDomain = zCreateOrderSchemaDom.parse(domain);
        const { addressInfo, corpCompanyId, ...rest } = safeDomain;
        const dto = {
            ...rest,
            ...addressInfo,
            scheduledDate: safeDomain.scheduledDate.toISOString(),
            // FR-007: колонка таблицы orders (snake_case), контракт v2.0 §5.4.
            // Ключ уходит только у корп-заказа: у частного его быть не должно вовсе.
            ...(domain.corpCompanyId ? { corp_company_id: domain.corpCompanyId } : {}),
        };
        return zCreateOrderSchemaDto.parse(dto);
    },
    /**
     * FR-007: тело UPDATE. Поля плательщика и снимка не отправляются никогда —
     * иначе полный DTO корп-заказа означал бы смену плательщика (CP008).
     */
    toUpdateOrderDto: (domain) => {
        const { corp_company_id, ...rest } = OrderMapper.toCreateOrderDto(domain);
        return zUpdateOrderSchemaDto.parse(rest);
    },
};
